import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core'
import { UserProfileService } from '../../../../../framework/authentication/services/user-profile.service'
import { UserProfile } from '../../../../../framework/authentication/models/UserProfile'
import { Address } from '../../../configurator/ui/models/address'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { LocaleService } from 'framework/locale/services/locale.service'

@UntilDestroy()
@Component({
    selector: 'mg-deal-user-profile',
    templateUrl: './deal-user-profile.component.html',
    styleUrls: ['./deal-user-profile.component.scss'],
})
export class DealUserProfileComponent implements OnInit {
    @Input()
    isEditing: boolean

    @Input()
    editingAllowed: boolean = true

    @Input()
    displayName: boolean = true

    @Input()
    displayLegalFirstNames: boolean = false

    @Input()
    displayPhonenumber: boolean = true

    @Input()
    displayAddress: boolean = true

    @Input()
    disabled: boolean = false

    @Output()
    // eslint-disable-next-line @angular-eslint/no-output-native
    change: EventEmitter<UserProfile> = new EventEmitter()

    @Output()
    changeHasFullAddress: EventEmitter<boolean> = new EventEmitter()

    @Output()
    initialized: EventEmitter<UserProfile> = new EventEmitter()

    telephoneNumberPattern: string = '\\D*(?:\\d\\D*){10,}'
    telephoneNumberRegex = new RegExp(`^${this.telephoneNumberPattern}$`)

    public userProfile: UserProfile | null
    language: string

    constructor(private userProfileService: UserProfileService, private localeService: LocaleService) {}

    /**
     * Are some name fields filled?
     *
     * @returns {boolean}
     */
    get hasName(): boolean {
        return !!(
            this.userProfile &&
            (this.userProfile.firstName || this.userProfile.lastName || this.userProfile.lastNamePrefix)
        )
    }

    /**
     * Are some name fields filled?
     *
     * @returns {boolean}
     */
    get hasLegalFirstNames(): boolean {
        return !!(this.userProfile && this.userProfile.legalFirstNames)
    }

    /**
     * Are all fields for a full name filled?
     *
     * @returns {boolean}
     */
    get hasFullName(): boolean {
        return !!this.userProfile && !!this.userProfile.firstName && !!this.userProfile.lastName
    }

    /**
     * Are some address fields filled?
     *
     * @returns {boolean}
     */
    get hasAddress(): boolean {
        return !!(
            this.userProfile &&
            this.userProfile.address &&
            (this.userProfile.address.street ||
                this.userProfile.address.houseNumber ||
                this.userProfile.address.houseNumberSuffix ||
                this.userProfile.address.zipcode ||
                this.userProfile.address.city)
        )
    }

    /**
     * Are all fields for an valid address filled?
     *
     * @returns {boolean}
     */
    get hasFullAddress(): boolean {
        return !!(
            this.userProfile &&
            this.userProfile.address &&
            this.userProfile.address.street &&
            this.userProfile.address.houseNumber &&
            this.userProfile.address.zipcode &&
            this.userProfile.address.city
        )
    }

    /**
     *
     * @returns {boolean}
     */
    get hasTelephoneNumber(): boolean {
        return !!this.userProfile && !!this.userProfile.telephoneNumber
    }

    /**
     *
     * @returns {boolean}
     */
    get validTelephoneNumber(): boolean {
        return this.hasTelephoneNumber && this.userProfile.telephoneNumber.search(this.telephoneNumberRegex) !== -1
    }

    /**
     *
     */
    get valid(): boolean {
        return (
            !!this.userProfile &&
            (!this.displayName || this.hasFullName) &&
            (!this.displayLegalFirstNames || this.hasLegalFirstNames) &&
            (!this.displayPhonenumber || this.validTelephoneNumber) &&
            (!this.displayAddress || this.hasFullAddress)
        )
    }

    ngOnInit() {
        this.language = this.localeService.language

        this.userProfileService.userProfile.pipe(untilDestroyed(this)).subscribe(userProfile => {
            if (userProfile === null) {
                this.userProfile = null
                return
            }

            // deep clone to prevent read only warnings
            this.userProfile = { ...JSON.parse(JSON.stringify(userProfile)) }

            if (!this.userProfile.address) {
                this.userProfile.address = new Address()
            }

            // if edit mode is not set explicitly, decide based on the information shown
            if (this.userProfile && this.editingAllowed && this.isEditing === undefined) {
                this.isEditing =
                    (this.displayName && !this.hasFullName) ||
                    (this.displayPhonenumber && !this.hasTelephoneNumber) ||
                    (this.displayAddress && !this.hasFullAddress)
            }

            this.initialized.emit(this.userProfile)
        })

        // Emit straight away
        this.onChange()
    }

    onChange() {
        const userProfile: UserProfile = {
            ...this.userProfile,
            address: {
                ...this.userProfile.address,
            },
        }
        this.userProfileService.userProfile.next(userProfile)
        this.change.emit(userProfile)
        this.changeHasFullAddress.emit(this.hasFullAddress)
    }
}
